'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

// FAQ data structure
type FAQItem = {
  question: string
  answer: string
}

// Photovoltaik FAQ data
const photovoltaikFAQs: FAQItem[] = [
  {
    question: 'Seid ihr die günstigsten bundesweit?',
    answer:
      'Bundesweit kann niemand so günstig Photovoltaikanlagen dauerhaft anbieten. Trotzdem setzen wir auf deutsche Handwerker in allen Gewerken, damit wir eine reibungslose Installation gewährleisten können.',
  },
  {
    question: 'Warum sind eure Lösungen so günstig?',
    answer:
      'Mit 20 Jahren Branchenerfahrungen, wissen wir welche Stellschrauben wichtig sind. Wir vermeiden eine steife und kostenintensive Unternehmensstruktur und arbeiten direkt mit den Herstellern zusammen. Gleichzeitig setzen wir auf deutsche Handwerker, um nicht an Qualität einzubüßen. Dadurch hat GoDeutschland insgesamt wesentlich weniger Kosten und kann flexibler und langfristiger agieren.',
  },
  {
    question: 'Wie sind eure Zahlungsmodalitäten?',
    answer:
      'Wir bieten drei Modelle an:<ul class="list-disc ml-6 mt-2 space-y-1"><li>100% Vorauszahlung = 1,5% Skonto</li><li>40% Nach Auftragsbestätigung, 50% nach Dachmontage-Montage, 10% nach AC-Installation (Elektrik) = 1% Skonto</li><li>0% Anzahlung = 40% nach Lieferung, 50% nach Dachmontage-Montage, 10% nach ACInstallation (Elektrik) = 0% Skonto</li></ul>',
  },
  {
    question: 'Wie schnell erfolgt die Installation?',
    answer:
      'Nach Ablauf der zweiwöchigen Widerrufsfrist, liefern wir innerhalb von 1-2 Wochen die Solarmodule und die Unterkonstruktion mit allen Komponenten und Zusatzteilen und fangen mit der Dachmontage an, die in einem Tag erfolgt. Nach ausdrücklicher Zustimmung durch Sie, können wir sofort vor Ablauf der Widerrufsfrist beginnen. Nach erfolgreicher Dachmontage, wird direkt mit der Installation der Wechselrichter und Batteriespeicher begonnen. Innerhalb von 3- 6 Wochen wird die Installation i.d.R. abgeschlossen. Plötzliche Lieferengpässe, Ausfälle durch Krankheit, schlechte Wetterbedingungen und weitere Einfüsse können die Installation natürlich verzögern. Trotz alledem garantieren wir eine schnelle Installation.',
  },
  {
    question: 'Was passiert bei defekten Komponenten und fehlerhafte Installation',
    answer:
      'Unsere Handwerksleistungen haben eine Gewährleistung von 5 Jahren. Wir alle sind Menschen. Sollte es zu Installationsfehlern kommen, werden wir kostenlos und sofort eine Reparatur vornehmen. Das ist für uns selbstverständlich. Bei defekten geleiferten Komponenten, werden ebenfalls sofort und kostenfrei einen Austausch vornehmen. Die einzelnen Komponenten haben eine Produktgarantie.',
  },
  {
    question: 'Garantieleistungen?',
    answer:
      '- Trina Vertex 2 + Module = 25 Jahre Produktgarantie auf die Verarbeitung und 30 Jahre Leistungsgarantie.<br>- Deyness Tower Batterie = 10 Jahre Garantie (7 + 3 Jahre nach Registrierung).<br>- Kostal Wechselrichter = 5 – 25 Jahre Garantie (5 Jahre Grundgarantie)<br>- Montage = 5 Jahre Gewährleistung .',
  },
  {
    question: 'Habe ich einen festen Ansprechpartner?',
    answer:
      'Ja. Über unsere eigene App kannst du uns sowieso ständig schreiben oder anrufen. Zudem betreiben wir auch mit einem Partner einen YouTube Kanal. Dort erfährst Du immer neusten Trends und Nachrichten zum Thema Energie.',
  },
  {
    question: 'Wie ist die Qualität der Photovoltaik Komponenten?',
    answer:
      'Wir arbeiten direkt mit den Produzenten zusammen. Daher können wir die Qualität ab Werk gewährleisten und arbeiten ständig mit den Produzenten an Verbesserungen. Gute Technik muss nicht teuer sein. Wir achten darauf, dass jeder sich hochwertige Komponenten leisten kann.',
  },
  {
    question: 'Empfehlungsprogramm?',
    answer:
      'Wenn Sie uns einen Kunden empfehlen, der eine Photovoltaikanlage durch GoDeutschland installieren lässt, erhalten Sie 200 Euro und Ihre Empfehlung erhält ebenfalls 200 € Rabatt auf die installierte Photovoltaikanlage.',
  },
]

// Accordion Item Component
const AccordionItem = ({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string
  answer: string
  isOpen: boolean
  onClick: () => void
}) => {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-300 dark:border-gray-700 dark:bg-gray-800/40">
      <button
        className="flex w-full cursor-pointer items-center justify-between px-6 py-5 text-left text-base font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:text-white dark:hover:bg-gray-800/70"
        onClick={onClick}
      >
        <span className="text-lg">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="ml-4 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-[#ff4500] dark:bg-orange-900/30 dark:text-orange-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div
              className="px-6 pt-0 pb-5 text-base text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{ __html: answer }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Main FAQ Section Component
export default function PhotovoltaikFAQ() {
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number | null>(null)

  // Intersection observer for section entry animation
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  })

  // Handle accordion toggle
  const toggleAccordion = (index: number) => {
    setOpenAccordionIndex(openAccordionIndex === index ? null : index)
  }

  return (
    <section ref={sectionRef} className="relative mb-16 py-24">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-2 inline-block text-lg font-medium text-[#ff4500]"
          >
            <span className="inline-block rounded-full bg-orange-100 px-3 py-1 text-[#ff4500] dark:bg-orange-900/30 dark:text-orange-400">
              Häufig gestellte Fragen
            </span>
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-6 text-3xl font-bold sm:text-4xl lg:text-5xl"
          >
            Haben Sie Fragen zu Photovoltaik?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-300"
          >
            Hier finden Sie Antworten auf die häufigsten Fragen zu unseren Photovoltaik-Lösungen
          </motion.p>
        </motion.div>

        {/* Accordion Items */}
        <div className="mx-auto max-w-7xl">
          {photovoltaikFAQs.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
            >
              <AccordionItem
                question={item.question}
                answer={item.answer}
                isOpen={openAccordionIndex === index}
                onClick={() => toggleAccordion(index)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
